import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import Icon from '@dustin-web/microsite-components/src/icon'
import Link from 'next/link'
import React from 'react'

type Props = {
  reviewsCount: number
  pageCount?: number
  onFetchPrevPage?: React.MouseEventHandler<HTMLButtonElement> | undefined
  onFetchNextPage?: React.MouseEventHandler<HTMLButtonElement> | undefined
}

const ProductReviewsFooter = ({
  reviewsCount,
  pageCount,
  onFetchNextPage,
  onFetchPrevPage,
}: Props) => {
  const { translate } = useTranslations()

  const pages = Math.floor(reviewsCount / 8)
  let start
  let end

  if (pageCount !== undefined) {
    start = pageCount * 8 + 1
    end = Math.min((pageCount + 1) * 8, reviewsCount)
  }

  if (reviewsCount === 1) {
    start = 1
    end = 1
  }

  return (
    <section>
      <div
        className={
          !reviewsCount
            ? 'review-footer__paginationContainerNoContent'
            : reviewsCount > 8
            ? 'review-footer__paginationContainerWithBtns'
            : 'review-footer__paginationContainer'
        }>
        <div className="review-footer__trustVoiceContainer">
          <TrustVoiceLogo />
        </div>
        {reviewsCount > 0 && (
          <nav
            aria-label={translate('Pagination_For_Reviews')}
            className="review-footer__reviewNav">
            <p>
              {reviewsCount <= 8
                ? translate('Product_Review_PaginationCountV2', { end, total: reviewsCount })
                : translate('Product_Review_PaginationCount', { start, end, total: reviewsCount })}
            </p>
            {reviewsCount > 8 && (
              <div>
                <button
                  aria-label={`${translate('GoTo_PreviousPage')}`}
                  className="review-footer__prevReviewsBtn"
                  disabled={pageCount === 0}
                  onClick={onFetchPrevPage}>
                  <Icon aria-label="arrow pointing left" name="arrow-left-small" />
                </button>
                <button
                  aria-label={`${translate('GoTo_NextPage')}`}
                  className="review-footer__nextReviewsBtn"
                  disabled={pageCount === pages}
                  onClick={onFetchNextPage}>
                  <Icon aria-label="arrow pointing right" name="arrow-right-small" />
                </button>
              </div>
            )}
          </nav>
        )}
      </div>
    </section>
  )
}

const TrustVoiceLogo = () => (
  <Link
    target="_blank"
    rel="noopener noreferrer"
    className="review-footer__link"
    href="https://trustvoice.com"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '.5em',
      color: '#8a8b8a',
      textDecoration: 'none',
    }}>
    <div>Verified by Trustvoice</div>
    <div
      style={{
        width: '1.5em',
        height: '1.761em',
        aspectRatio: '500 / 587',
      }}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="103 86 362 425">
        <defs>
          <clipPath id="tvclippath" clipPathUnits="userSpaceOnUse">
            <path d="M0 425.197h425.197V0H0Z" />
          </clipPath>
        </defs>
        <g clipPath="url(#tvclippath)" transform="matrix(1.33333 0 0 -1.33333 0 566.93)">
          <path
            d="M0 0c0 74.363-60.283 134.646-134.646 134.646-74.362 0-134.645-60.283-134.645-134.646 0-55.478 33.561-103.104 81.481-123.724l-28.422-57.705 126.264 54.406C-37.572-108.593 0-58.697 0 0"
            style={{
              fill: '#183965',
              fillOpacity: 1,
              fillRule: 'nonzero',
              stroke: 'none',
            }}
            transform="translate(347.592 225.257)"
          />
          <path
            d="M0 0c0 50.715 41.113 91.828 91.828 91.828S183.656 50.715 183.656 0c0-37.836-22.888-70.317-55.57-84.379l19.384-39.356-86.112 37.106C25.624-74.06 0-40.031 0 0"
            style={{
              fill: '#fff',
              fillOpacity: 1,
              fillRule: 'nonzero',
              stroke: 'none',
            }}
            transform="translate(121.118 225.059)"
          />
          <path
            d="m0 0 16.839-39.85 43.104-3.701-32.696-28.33 9.8-42.137L0-91.676l-37.047-22.342 9.8 42.137-32.696 28.33 43.104 3.701z"
            style={{
              fill: '#7092bf',
              fillOpacity: 1,
              fillRule: 'nonzero',
              stroke: 'none',
            }}
            transform="translate(212.946 286.963)"
          />
        </g>
      </svg>
    </div>
  </Link>
)

export default ProductReviewsFooter
